import {
  ClassicKeyTravel,
  ClassicKeyTravelInfo,
  KeyTravelFamily,
  KeyTravelList,
  KeyTravelWriteList,
} from '../../../../types/keyTravel'
import { KeyTravelFeature } from '../../../../features/key-travel/contract'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { calculateChecksum } from '../../protocols/common'

import { CMD_OHID, OHID_CMD_WRITE, PROTOCOL_CONFIG } from '../../protocols/cmd'
const MASTER_REPORT_ID = PROTOCOL_CONFIG.master_report_id
const REPORT_ID = PROTOCOL_CONFIG.report_id

/**
 * @file CH585MKeys.ts
 * @author Rapoo Device SDK Team
 * @date 2026-03-31
 * @description CH585M 键程功能
 */
export class OHIDTravel extends KeyTravelFeature {
  /**
   * 生成 SIGNAL 驱动参数数据包
   * 每个 SIGNAL 项包含: mKey(2字节) + MM(1字节) + Reset(1字节) = 4字节
   *
   * @param options - 参数选项
   * @returns ArrayBuffer 数据包
   */
  private ohidDriverSIGNAL(options: {
    write: boolean
    mKey: number[]
    MM: number[]
    Reset: number[]
    len: number
  }): ArrayBuffer {
    const buffer = new ArrayBuffer(64 * 4)
    const view = new DataView(buffer)

    const cmd = options.write
      ? OHID_CMD_WRITE(CMD_OHID.ohid_cmd_driver_single)
      : CMD_OHID.ohid_cmd_driver_single
    let offset = 0

    // 写入头部
    view.setUint8(offset++, MASTER_REPORT_ID) // HEAD
    view.setUint8(offset++, 0) // length
    view.setUint8(offset++, cmd) // cmd
    view.setUint8(offset++, 0) // checksum

    // 写入数据：每个 SIGNAL 项包含 1 个 16 位值和 2 个 8 位值（共 4 字节）
    for (let i = 0; i < options.len; i++) {
      // 1 个 16 位值
      view.setUint16(offset, options.mKey[i] || 0, true)
      offset += 2

      // 2 个 8 位值
      view.setUint8(offset, options.MM[i] || 0)
      offset++
      view.setUint8(offset, options.Reset[i] || 0)
      offset++
    }

    // 更新长度
    const dataLength = offset - 4
    view.setUint8(1, dataLength)

    // 计算校验和
    const checksum = calculateChecksum(view, dataLength, 4)
    view.setUint8(3, checksum & 0xff)

    return buffer.slice(0, offset)
  }

  /**
   * ohidDriverSIGNAL 的逆函数
   * 从 ArrayBuffer 解析 SIGNAL 驱动参数配置
   *
   * @param buffer - ArrayBuffer 数据包
   * @param len - 数据项的长度
   * @returns 解析出的 options 参数
   */
  private parseDriverSIGNALOptions(
    buffer: ArrayBuffer,
    len: number,
  ): {
    write: boolean
    mKey: number[]
    MM: number[]
    Reset: number[]
  } {
    const view = new DataView(buffer)
    let offset = 0

    // 1. 解析头部
    const HEAD = view.getUint8(offset++) // 协议头
    const dataLength = view.getUint8(offset++) // 数据长度
    const cmd = view.getUint8(offset++) // 命令
    //const checksum = view.getUint8(offset++) // 校验和
    offset++

    // 2. 验证协议头
    if (HEAD !== 0xa5) {
      throw new Error(`无效的协议头: 期望 0xa5, 实际 0x${HEAD.toString(16)}`)
    }

    // 3. 解析写入/读取标志
    const isWrite = (cmd & 0x80) !== 0
    const baseCmd = cmd & 0x7f

    // 验证命令是否为 SIGNAL 驱动参数命令
    if (baseCmd !== CMD_OHID.ohid_cmd_driver_single) {
      throw new Error(
        `无效的命令: 期望 0x${CMD_OHID.ohid_cmd_driver_single.toString(16)}, 实际 0x${baseCmd.toString(16)}`,
      )
    }

    // 4. 验证数据长度
    // 每个 SIGNAL 项: mKey(2字节) + MM(1字节) + Reset(1字节) = 4字节
    const expectedDataLength = len * 4
    if (dataLength < expectedDataLength) {
      throw new Error(`数据长度不匹配: 期望 ${expectedDataLength}, 实际 ${dataLength}`)
    }

    // 5. 解析数据数组
    const mKey: number[] = []
    const MM: number[] = []
    const Reset: number[] = []

    for (let i = 0; i < len; i++) {
      if (offset + 4 > view.byteLength) {
        throw new Error(`缓冲区越界: 尝试读取位置 ${offset}, 但缓冲区长度只有 ${view.byteLength}`)
      }

      // 读取 1 个 16 位值
      mKey.push(view.getUint16(offset, true))
      offset += 2

      // 读取 2 个 8 位值
      MM.push(view.getUint8(offset))
      offset++

      Reset.push(view.getUint8(offset))
      offset++
    }

    return {
      write: isWrite,
      mKey,
      MM,
      Reset,
    }
  }

  /**
   * 异步分批从设备获取键位数据并存入 buffer
   * 改进版本：更好的错误处理、进度回调、可取消
   *
   * @param allKeys 所有键位的 key 数组
   * @param batchSize 每批处理的键位数
   * @param onProgress 进度回调函数
   * @param signal 取消信号
   * @returns 包含所有键位数据的 ArrayBuffer
   */
  async batchGetKeyDataFromDeviceAsync(
    allKeys: number[],
    batchSize: number = 15,
    onProgress?: (progress: {
      batchIndex: number
      batchCount: number
      processed: number
      total: number
      percent: number
    }) => void,
    signal?: AbortSignal,
  ): Promise<ArrayBuffer> {
    const totalKeys = allKeys.length

    // 计算总批次数
    const batchCount = Math.ceil(totalKeys / batchSize)

    // 创建结果 buffer
    const resultBuffer = new ArrayBuffer(totalKeys * 3)
    const resultView = new DataView(resultBuffer)
    let resultOffset = 0

    console.log(
      `异步处理开始: 总键位数=${totalKeys}, 批次大小=${batchSize}, 总批次数=${batchCount}`,
    )

    // 检查取消信号
    if (signal?.aborted) {
      throw new Error('操作已被取消')
    }

    // 使用 Promise.allSettled 并行处理批次（可选）
    // 或者使用 for 循环串行处理以保证顺序

    for (let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      // 检查取消信号
      if (signal?.aborted) {
        console.log('操作被用户取消')
        throw new Error('操作已被取消')
      }

      const startIdx = batchIndex * batchSize
      const endIdx = Math.min(startIdx + batchSize, totalKeys)
      const currentBatchSize = endIdx - startIdx

      // 触发进度回调
      if (onProgress) {
        onProgress({
          batchIndex: batchIndex + 1,
          batchCount,
          processed: startIdx + currentBatchSize,
          total: totalKeys,
          percent: Math.round(((batchIndex + 1) / batchCount) * 100),
        })
      }

      console.log(`处理批次 ${batchIndex + 1}/${batchCount} (${currentBatchSize} 个键位)`)

      try {
        // 处理当前批次
        await this.processSingleBatchAsync(
          false,
          allKeys.slice(startIdx, endIdx),
          batchIndex,
          resultView,
          resultOffset,
          signal,
        )

        resultOffset += currentBatchSize * 2

        console.log(`批次 ${batchIndex + 1} 处理完成`)
      } catch (error) {
        console.error(`批次 ${batchIndex + 1} 处理失败:`, error)
        throw new Error(`批次 ${batchIndex + 1} 失败: ${error}`)
      }

      // 批次间延迟（可配置）
      if (batchIndex < batchCount - 1) {
        await this.delayAsync(50) // 异步延迟
      }
    }

    console.log(`异步处理完成: 总共处理 ${totalKeys} 个键位`)
    return resultBuffer
  }

  /**
   * 异步处理单个批次
   */
  async processSingleBatchAsync(
    write: false,
    batchKeys: number[],
    _batchIndex: number,
    resultView: DataView,
    resultOffset: number,
    signal?: AbortSignal,
  ): Promise<void> {
    const batchSize = batchKeys.length

    // 检查取消信号
    if (signal?.aborted) {
      return Promise.reject(new Error('操作已被取消'))
    }

    // 准备请求参数
    const requestOptions = {
      write: write,
      mKey: batchKeys,
      MM: Array(batchSize).fill(0),
      Reset: Array(batchSize).fill(0),
      len: batchSize,
    }

    // 生成请求包
    const packetInfo = this.ohidDriverSIGNAL(requestOptions)
    const dataView = new DataView(packetInfo)

    // 准备发送数据
    const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
    const outputData = new Uint8Array(64)
    outputData.set(paddedData, 0)
    //获取 report ID
    const outputReportId = REPORT_ID
    const inputReportId = REPORT_ID

    // 发送请求（异步）
    const response = await this.device.sendAndReceive(
      outputReportId,
      outputData,
      inputReportId,
      500,
    )

    // 解析响应
    const responseOptions = await this.parseDriverSIGNALOptions(
      response.data.buffer as ArrayBuffer,
      batchSize,
    )

    // 将数据写入结果 buffer
    let offset = resultOffset
    for (let i = 0; i < batchSize; i++) {
      resultView.setUint8(offset, responseOptions.MM[i] || 0)
      offset++
      resultView.setUint16(offset, responseOptions.Reset[i] || 0, true)
      offset++
    }
  }

  /**
   * 异步延迟函数
   */
  private delayAsync(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms)
    })
  }

  /**
   * 获取键盘所有键位的单键程及死区
   */
  async getKeyTravel(rt: number): Promise<KeyTravelList> {
    console.log('[CH585M] get key travel')
    if (rt >= 0 && rt <= 2) {
      const globalKeyTravel: ClassicKeyTravel = {
        absoluteTrigger: 2,
        rtTriggerSensitivity: 0,
        rtResetSensitivity: 0,
        rtTriggerEnable: false,
        absoluteTriggerEnable: false,
        bottomDeadZone: 0,
      }
      return [
        {
          family: KeyTravelFamily.Classic,
          index: 0,
          keyTravel: globalKeyTravel,
        },
      ]
    }

    // 获取所有键位
    const allKeys = this.device.manifest.layout.keys.flatMap((row) => row.map((key) => key.key))
    // 创建取消控制器
    const controller = new AbortController()

    // 进度回调
    const onProgress = (progress: any) => {
      console.log(`进度: ${progress.percent}% (${progress.processed}/${progress.total})`)
    }

    // 设置超时
    const timeoutId = setTimeout(() => {
      controller.abort()
      console.log('操作超时，已取消')
    }, 30000) // 30秒超时
    // 异步获取常规按键数据
    const allKeysData = await this.batchGetKeyDataFromDeviceAsync(
      allKeys,
      15, // 每批15个
      onProgress,
      controller.signal,
    )
    clearTimeout(timeoutId)

    console.log('数据获取完成')

    const readPromises: ClassicKeyTravelInfo[] = []

    for (let i = 0; i < allKeys.length; i++) {
      const startOffset = i * 2
      // 创建当前键位的 DataView（2个字节）
      const keyDataBuffer = allKeysData.slice(startOffset, startOffset + 2)
      const keyDataView = new DataView(keyDataBuffer)
      const keyTravel: ClassicKeyTravel = {
        absoluteTrigger: keyDataView.getUint8(1),
        rtTriggerSensitivity: 0,
        rtResetSensitivity: 0,
        rtTriggerEnable: false,
        absoluteTriggerEnable: true,
        bottomDeadZone: 0,
      }
      const result: ClassicKeyTravelInfo = {
        family: KeyTravelFamily.Classic,
        index: allKeys[i],
        keyTravel,
      }
      readPromises.push(result)
    }
    return (await Promise.all(readPromises)).flat()
  }

  /**
   * 批量设置多个指定键位的单键程及死区
   * @param keyTravelList 多键位的键程配置列表
   * @returns Promise<void>
   */
  async setKeyTravel(keyTravelList: KeyTravelWriteList, index?: number): Promise<void[] | void> {
    if (!Array.isArray(keyTravelList) || keyTravelList.length === 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Invalid key travel list')
    }

    if (index !== undefined && index >= 0 && index <= 2) {
    }

    // 批量处理每个键位的配置（并发执行，保证效率）
    const totalKeys = keyTravelList.length
    const batchSize = 15
    // 计算总批次数
    const batchCount = Math.ceil(totalKeys / batchSize)

    console.log(`处理开始: 总键位数=${totalKeys}, 批次大小=15, 总批次数=${batchCount}`)

    // 使用 Promise.allSettled 并行处理批次（可选）
    // 或者使用 for 循环串行处理以保证顺序

    for (let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      const startIdx = batchIndex * batchSize
      const endIdx = Math.min(startIdx + batchSize, totalKeys)
      const currentBatchSize = endIdx - startIdx

      console.log(`处理批次 ${batchIndex + 1}/${batchCount} (${currentBatchSize} 个键位)`)
      const travelList = keyTravelList.slice(startIdx, endIdx)

      const mKey = travelList.map((item) => item.index)
      const MM = travelList.map((item) => {
        if (item.family !== KeyTravelFamily.Classic) {
          throw new DeviceError(
            DeviceErrorCode.INVALID_PARAM,
            'OHID setKeyTravel requires family: KeyTravelFamily.Classic',
          )
        }
        return item.keyTravel.absoluteTrigger
      })

      try {
        // 处理当前批次
        // 准备请求参数
        const requestOptions = {
          write: true,
          mKey: mKey,
          MM: MM,
          Reset: MM,
          len: batchSize,
        }

        // 生成请求包
        const packetInfo = this.ohidDriverSIGNAL(requestOptions)
        const dataView = new DataView(packetInfo)

        // 准备发送数据
        const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
        const outputData = new Uint8Array(64)
        outputData.set(paddedData, 0)
        //获取 report ID
        const outputReportId = REPORT_ID
        const inputReportId = REPORT_ID

        // 发送请求（异步）
        const response = await this.device.sendAndReceive(
          outputReportId,
          outputData,
          inputReportId,
          500,
        )

        // 解析响应
        await this.parseDriverSIGNALOptions(response.data.buffer as ArrayBuffer, batchSize)
        console.log(`批次 ${batchIndex + 1} 处理完成`)
      } catch (error) {
        console.error(`批次 ${batchIndex + 1} 处理失败:`, error)
        throw new Error(`批次 ${batchIndex + 1} 失败: ${error}`)
      }

      // 批次间延迟（可配置）
      if (batchIndex < batchCount - 1) {
        await this.delayAsync(50) // 异步延迟
      }
    }
  }
}
